import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { AuthContext } from '../components/auth/AuthProvider';
import { buildApiUrl } from '../utils/apiUtils';

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #0a0b14;
  padding: 20px;
`;

const PaymentCard = styled.div`
  max-width: 500px;
  width: 100%;
  background-color: #171923;
  border-radius: 12px;
  padding: 40px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  text-align: center;
`;

const IconContainer = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: #4CAF50;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 24px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 400;
  margin-bottom: 16px;
  color: #fff;
`;

const Message = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: #e1e1e1;
  margin-bottom: 40px;
`;

const Button = styled.button`
  display: block;
  width: 100%;
  padding: 14px;
  margin-bottom: 16px;
  border-radius: 50px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &.primary {
    background-color: #A961ED;
    color: white;
    
    &:hover {
      background-color: #9050c9;
    }
  }
  
  &.secondary {
    background-color: transparent;
    color: white;
    border: 1px solid #2d3748;
    
    &:hover {
      background-color: rgba(45, 55, 72, 0.5);
    }
  }
`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = React.useContext(AuthContext);
  const [isVerifying, setIsVerifying] = useState(true);
  const [orderVerified, setOrderVerified] = useState(false);
  const [hasProcessed, setHasProcessed] = useState(false);

  useEffect(() => {
    // Parse query parameters to get transaction details
    const params = new URLSearchParams(location.search);
    const transactionId = params.get('transaction_id');

    // Get order ID from location state if available
    const orderId = location.state?.orderDetails?.id || transactionId;

    // Create a unique key for this transaction to prevent duplicate processing
    const processKey = `payment_processed_${orderId || transactionId}`;

    // Check if we've already processed this payment
    const alreadyProcessed = localStorage.getItem(processKey);

    if (alreadyProcessed) {
      console.log('Payment already processed, skipping API calls');
      setIsVerifying(false);
      setOrderVerified(true);
      setHasProcessed(true);
      return;
    }

    // Mark as processed to prevent duplicate processing
    localStorage.setItem(processKey, 'true');
    setHasProcessed(true);

    // Clear cart after successful payment
    localStorage.setItem('cart', '[]');
    window.dispatchEvent(new Event('cartUpdated'));

    // Log transaction details for debugging
    console.log('Processing payment:', transactionId, 'Order ID:', orderId);

    // Call the auto-complete endpoint for digital orders
    const autoCompleteDigitalOrder = async () => {
      try {
        // Only proceed if we have an order ID
        if (orderId) {
          const response = await axios.get(
            buildApiUrl(`payment/success/${orderId}`)
          );
          console.log('Auto-complete response:', response.data);

          // If the order was auto-completed, mark it as verified
          if (response.data.success) {
            setOrderVerified(true);
            setIsVerifying(false);
          }
        }
      } catch (error) {
        console.error('Error auto-completing digital order:', error);
      }
    };

    // You could verify the payment status here by making an API call
    // to your backend to confirm the payment was successful
    const checkOrderStatus = async () => {
      try {
        setIsVerifying(true);

        // First try to auto-complete digital orders
        await autoCompleteDigitalOrder();

        if (user && user.id) {
          // Poll the order status a few times to check if it's been updated by webhook
          let attempts = 0;
          const maxAttempts = 5;

          const checkStatus = async () => {
            try {
              const response = await axios.get(
                buildApiUrl('orders'),
                { headers: { 'X-User-ID': user.id } }
              );

              if (response.data.success) {
                // Find the order by ID
                const order = response.data.orders.find(o => o.id === parseInt(orderId));

                if (order && order.status === 'completed') {
                  setOrderVerified(true);
                  setIsVerifying(false);
                  return true;
                }
              }

              return false;
            } catch (error) {
              console.error('Error checking order status:', error);
              return false;
            }
          };

          // Poll at 3-second intervals
          const poll = async () => {
            const success = await checkStatus();
            if (success) return;

            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(poll, 3000);
            } else {
              // After max attempts, assume everything is fine anyway
              setIsVerifying(false);
            }
          };

          poll();
        } else {
          // If no user is available, just assume everything is fine
          setIsVerifying(false);
        }
      } catch (error) {
        console.error('Error verifying order:', error);
        setIsVerifying(false);
      }
    };

    // Verify order status
    checkOrderStatus();

    // Optional: Track conversion/analytics
    if (window.gtag) {
      window.gtag('event', 'purchase_complete', {
        transaction_id: transactionId
      });
    }
  }, [location, user]);

  return (
    <PageContainer>
      <PaymentCard>
        <IconContainer>
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" color="white">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </IconContainer>

        <Title>Payment Successful!</Title>

        <Message>
          Thank you for your purchase. Your star map is now being processed.
          {isVerifying ? (
            <p className="text-sm mt-2">Verifying your order status...</p>
          ) : orderVerified ? (
            <p className="text-sm mt-2 text-green-600">Your order has been confirmed! You can now view it in your profile.</p>
          ) : (
            <p className="text-sm mt-2">You'll receive a confirmation email shortly with details about your order.</p>
          )}
        </Message>

        <Button
          className="primary"
          onClick={() => {
            navigate('/profile');
            // Use setTimeout to ensure navigation completes before attempting to scroll
            setTimeout(() => {
              // Look for the section with the heading "Your Star Maps"
              const mapsSectionHeading = Array.from(document.querySelectorAll('h3')).find(
                heading => heading.textContent.trim() === 'Your Star Maps'
              );

              if (mapsSectionHeading) {
                // Scroll to the heading's parent element
                mapsSectionHeading.scrollIntoView({ behavior: 'smooth' });
              }
            }, 500);
          }}
        >
          Go to Profile
        </Button>

        <Button
          className="secondary"
          onClick={() => navigate('/')}
        >
          Return to Home
        </Button>
      </PaymentCard>
    </PageContainer>
  );
};

export default PaymentSuccess; 